import { createContext, useContext, Fragment } from 'react'
import { Helmet } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { TFunction } from "i18next"
import { Formik, Form, Field } from "formik"
import { ChevronRightIcon } from "@heroicons/react/20/solid"
import clsx from "clsx"

import { Outlet, useParams, Link, useNavigate } from "../navigation"
import { useDesigns } from "./DesignsProvider"
import { useRoles } from '../user/Auth'
import { useSingleTeamMangement } from "../Management"
import { LinkedTabCard } from '../components/TabCard'
import DesignComments from './DesignComments'
import { DesignRegistrationTasks } from './DesignTasks'
import { IconEdit } from "../components/icons"
import DeleteButton from '../components/DeleteButton'
import { FocusedBrowser } from '../documents/FocusedBrowser'
import { design_registration, design_family } from '../data'
import Modal from "../components/Modal"
import Editor from "../components/Editor"
import { designFamilyUrl } from "./utils"
import { Agent, nameOf } from '../agents/utils'
import Image from '../components/Image'
import { useBackend } from '../BackendProvider'
import { FamilyBreadCrumb } from "../patents/Family"
import { DesignFamily, DesignRegistration as DesignRegistrationType } from './designs'
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid"

export default function DesignRegistration() {
    const {t} = useTranslation()

    const {hasDocuments} = useRoles()
    const {reference} = useParams()
    const {
        registrationByReference,
        familyById,
        designersByRegistrationId,
        ownersByRegistrationId,
        contactsByRegistrationId,
        applicantsByRegistrationId,
    } = useDesigns()

    const registration = registrationByReference[reference]

    if (registration === undefined) return null

    const family = familyById[registration.familyId]

    if (family === undefined) return null

    // Attach agents to registration object for display
    const enrichedRegistration = {
        ...registration,
        designer: designersByRegistrationId[registration.registrationId]?.[0],
        owner: ownersByRegistrationId[registration.registrationId]?.[0],
        contact: contactsByRegistrationId[registration.registrationId]?.[0],
        applicant: applicantsByRegistrationId[registration.registrationId]?.[0],
    }

    return (
        <DesignRegistrationProvider {...{ family, registration: enrichedRegistration }}>
            <Helmet>
                <title>{reference} | {t('designs')} | Patent Cockpit</title>
            </Helmet>
            <div className="portfolio-menu">
                <h2 className="flex flex-row gap-1 md:gap-4 whitespace-nowrap shrink min-w-0 grow pr-4">
                    <DesignRegistrationBreadcrumbs {...{ family, registration }} />
                </h2>
            </div>
            <div className="main-content bg-pcx-100 pt-0">
                <div className="w-full md:w-fit">
                    <div className="lg:min-w-5xl xl:min-w-6xl">
                        <LinkedTabCard links={[
                            { to: '.', label: t('overview') },
                            { to: 'figures', label: t('figures') },
                            { to: 'classes', label: t('design-classes') },
                            hasDocuments && { to: 'documents', label: t('documents') },
                        ].filter(Boolean)} />
                    </div>
                </div>
            </div>
        </DesignRegistrationProvider>
    )
}

const DesignRegistrationContext = createContext({
    registration: null as DesignRegistrationType | null,
    family: null as DesignFamily | null,
})

function DesignRegistrationProvider({registration, family, children}: {registration: DesignRegistrationType, family: DesignFamily, children: React.ReactNode}) {
    return <DesignRegistrationContext.Provider value={{registration, family}}>{children}</DesignRegistrationContext.Provider>
}

export function useDesignRegistration() {
    return useContext(DesignRegistrationContext)
}

// TODO Use BreadCrumbs.tsx
function ModernChevronRight({className}: {className?: string}) {
    return <ChevronRightIcon className={clsx("h-5 w-5 flex-shrink-0 text-gray-600 self-center", className)} aria-hidden="true" />
}

function DesignRegistrationBreadcrumbs({ family, registration }: { family: DesignFamily, registration: DesignRegistrationType }) {
    const { t } = useTranslation()

    return (
        <>
            <Link to="/designs/portfolio" className={clsx("hidden sm:block", "modern-h2")}>{t('portfolio')}</Link>
            <ModernChevronRight className="hidden sm:block" />
            <FamilyBreadCrumb {...{
                to: designFamilyUrl(family),
                reference: family.reference,
                familyName: family.description
            }} />
            <ModernChevronRight />
            <span className="modern-h2">{registration.reference}</span>
        </>
    )
}

export function DesignRegistrationOverview() {
    const { registration, family } = useDesignRegistration()

    if (!registration || !family) return null

    return <>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(40rem,48rem)_400px] grid-cols-auto gap-2 grid-flow-dense divide-pcx-200 max-lg:divide-y-2 lg:divide-x-2">
            <div>
                <DesignRegistrationDetails {...{ registration, family }} />
            </div>
            <div className='space-y-5 max-lg:pt-2 lg:pl-2'>
                <DesignRegistrationTasks registrationId={registration.registrationId} />
                <DesignComments {...{ registrationId: registration.registrationId }} />
            </div>
        </div>
        <Outlet />
    </>
}

function DesignRegistrationDetails({ registration, family }: { registration: EnrichedRegistration, family: DesignFamily }) {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { isEditUser } = useRoles()
    const { deleteRegistration, figuresByRegistrationId } = useDesigns()
    const { imagesLookup, images } = useBackend()

    const familyUrl = designFamilyUrl(family)
    const deleteQuestion = t('delete-design-registration-name', { name: registration.reference, country: registration.countryCode })

    const hasRegistrationImage = imagesLookup[design_registration]?.[registration.registrationId] !== undefined
    const hasFamilyImage = imagesLookup[design_family]?.[family.designFamilyId] !== undefined

    // Get the single figure marked for overview (if any)
    const overviewFigure = (figuresByRegistrationId[registration.registrationId] || [])
        .find(fig => fig.showOnOverview)
    const overviewFigureImage = overviewFigure?.imageId ? images.find(i => i.imageId === overviewFigure.imageId) : undefined
    const hasFigureImage = overviewFigureImage !== undefined

    // Determine which image to show: figure > registration > family
    const getImageLabel = () => {
        if (hasFigureImage) return null
        if (hasRegistrationImage) return t('registration-image')
        if (hasFamilyImage) return t('family-image')
        return t('design-image')
    }

    return <>
        <div className="flex flex-col sm:flex-row gap-4 pb-2 border-b-2 border-pcx-200">
            {/* Image display: figure image takes priority if selected */}
            <div className="w-40 shrink-0">
                {getImageLabel() && <div className="text-xs text-pcx-500 mb-1">{getImageLabel()}</div>}
                {hasFigureImage ? (
                    <img
                        src={overviewFigureImage.url}
                        alt={t('figure')}
                        className="w-full max-h-32 object-contain rounded border"
                    />
                ) : (
                    <Image {...{
                        entity: design_registration,
                        entityId: registration.registrationId,
                        fallback: hasFamilyImage && !hasRegistrationImage
                            ? { entity: design_family, entityId: family.designFamilyId }
                            : undefined,
                        isEditable: isEditUser,
                        title: registration.reference + ': ' + family.description,
                        text: family.description
                    }} />
                )}
            </div>
            <div className='space-y-2 grow'>
                <div className="flex flex-row gap-1">
                    <h2 className="grow">{registration.reference}: {family.description}</h2>
                    {isEditUser && <>
                        <Link className="btn-primary shrink-0 p-px w-5 h-5" to="edit"><IconEdit /></Link>
                        <DeleteButton className="btn-warn shrink-0 p-px w-5 h-5" deleteAction={() => deleteRegistration(registration).then(() => navigate(familyUrl))} question={deleteQuestion} />
                    </>}
                </div>
                <p className="max-w-prose">{family.description}</p>
            </div>
        </div>
        <h4 className="mt-4 mb-4">{t('bibliographic-data')}</h4>
        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-x-4 gap-y-2">
            {registrationFields.map(({ name, render }) => {
                const output = render(registration, family, t)
                if (!output) return null
                return <Fragment key={name}>
                    <div className="text-right text-gray-500">{t(name)}</div>
                    <div>{output}</div>
                </Fragment>
            })}
        </div>
    </>
}

type EnrichedRegistration = DesignRegistrationType & { designer?: Agent, owner?: Agent, contact?: Agent, applicant?: Agent }

const registrationFields = [
    {
        name: 'design-title',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.title,
    },
    {
        name: 'designType',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) =>
            registration.designType ? t(`design-type-${registration.designType.toLowerCase()}`) : null,
    },
    {
        name: 'applicationNumber',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.applicationNumber,
    },
    {
        name: 'filing-date',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.applicationDate,
    },
    {
        name: 'registration-number',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.registrationNumber,
    },
    {
        name: 'registration-date',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.registrationDate,
    },
    {
        name: 'publication-date',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.publicationDate,
    },
    {
        name: 'status',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => t(registration.status),
    },
    {
        name: 'country',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.countryCode,
    },
    {
        name: 'renewal-date',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.renewalDate,
    },
    {
        name: 'expiry-date',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.expiryDate,
    },
    {
        name: 'abandonmentDate',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.abandonmentDate,
    },
    {
        name: 'external-link',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) =>
            registration.officeUrl ? (
                <a href={registration.officeUrl} target="_blank" rel="noreferrer"
                   className="underline flex flex-row gap-1 items-center text-pcx-600">
                    <ArrowTopRightOnSquareIcon className='h-4 w-4' /> {t('external-link')}
                </a>
            ) : null,
    },
    {
        name: 'designer',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.designer ? nameOf(registration.designer) : null,
    },
    {
        name: 'applicant',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.applicant ? nameOf(registration.applicant) : null,
    },
    {
        name: 'owner',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.owner ? nameOf(registration.owner) : null,
    },
    {
        name: 'contact-person',
        render: (registration: EnrichedRegistration, family: DesignFamily, t: TFunction) => registration.contact ? nameOf(registration.contact) : null,
    },
]

export function DesignClasses() {
    const { t } = useTranslation()
    const { isEditUser } = useRoles()
    const { registration } = useDesignRegistration()
    const { designClassesByRegistrationId, deleteDesignClass } = useDesigns()

    if (!registration) return null

    const designClasses = designClassesByRegistrationId[registration.registrationId] || []

    return <>
        <div className="flex flex-row justify-between items-start mb-2">
            <h3 id="design_classes">{t('design-classes')}</h3>
            {isEditUser && <Link to="add" className="btn-secondary py-0.5 text-sm whitespace-nowrap">{t('add')}</Link>}
        </div>
        <div className="grid grid-cols-[80px_1fr_44px] gap-3">
            {designClasses.map(c => <Fragment key={c.classId}>
                <div className="text-right text-2xl text-pcx-400">{c.classNumber}</div>
                <div className="pt-1" dangerouslySetInnerHTML={{ __html: c.description ?? "" }}></div>
                <div className="flex flex-row gap-1 items-start">{isEditUser && <>
                    <Link className="btn-primary p-px w-5 h-5" to={`${c.classId}/edit`}><IconEdit /></Link>
                    <DeleteButton deleteAction={() => deleteDesignClass(c)} className="btn-warn p-px w-5 h-5"></DeleteButton>
                </>}</div>
            </Fragment>)}
        </div>
        <Outlet />
    </>
}

export function PostDesignClass() {
    const { t } = useTranslation()
    const { team } = useSingleTeamMangement()
    const { registration } = useDesignRegistration()
    const { designClassesByRegistrationId, postDesignClass, reloadDesignClasses } = useDesigns()
    const { classId } = useParams()
    const navigate = useNavigate()

    if (!registration) return null

    const registrationId = registration.registrationId
    const designClasses = designClassesByRegistrationId[registrationId] || []
    const designClass = designClasses.find(c => c.classId === parseInt(classId ?? "-1"))

    const existingNumbers = designClasses
        .filter(c => c.classId !== designClass?.classId)
        .map(c => c.classNumber)

    const initialValues = designClass ?? {
        realm: team?.realm,
        registrationId: registrationId,
        classNumber: "01",
        description: "",
    }

    return (
        <Modal>
            <Formik
                initialValues={initialValues}
                onSubmit={values => {
                    postDesignClass(values).then(() => {
                        reloadDesignClasses()
                        navigate("..")
                    })
                }}
                enableReinitialize
                validate={values => {
                    const errors: Record<string, string> = {}
                    if (existingNumbers.includes(values.classNumber))
                        errors.classNumber = t('is-already-used')
                    return errors
                }}
            >{({ errors, touched }) =>
                <Form className="max-w-2xl">
                    <div className="p-4 bg-white flex flex-col gap-2">
                        <h4>{t('design-class-for', { name: registration?.reference })}</h4>
                        <label className="w-full">
                            <div className="mb-1">
                                <span className="inline label">{t('class-number')}</span>
                                {errors.classNumber && touched.classNumber && <span className="ml-2 inline text-red-700"> {errors.classNumber}</span>}
                            </div>
                            <Field
                                name="classNumber"
                                type="text"
                                className="form-input w-full"
                                placeholder="01-32"
                                title={t('invalid-format')}
                            />
                        </label>

                        <div>
                            <label htmlFor="description" className="label mb-1">{t('description')}</label>
                            <div className="w-full max-sm:pb-10 h-[16rem] sm:h-[24rem] md:min-w-[40rem]">
                                <Editor name="description" />
                            </div>
                        </div>
                    </div>
                    <div className="p-4 bg-pcx-200 flex flex-row-reverse gap-4">
                        <input type="submit" className="btn-primary" value={t('save')} />
                        <Link to={-1} className="btn-secondary">{t('cancel')}</Link>
                    </div>
                </Form>
            }</Formik>
        </Modal>
    )
}

export function DesignRegistrationDocuments() {
    const { registration, family } = useDesignRegistration()

    if (!registration) return null

    return <FocusedBrowser entity={design_registration} internalReference={registration.reference} parent={family.reference} parentName={family.name} />
}
